import { RoomsService as RoomsApi } from '../../shared/api/generated/rooms/rooms.service';
import { computed, effect, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { AppStore } from '../../state/app.repository';
import { StatsTarget, StreamStatsService } from './stream-stats.service';
import { User, UserData, UserStore } from '../../state/user.repository';
import {
  AudioActivityPayload,
  AudioInputPayload,
  AudioOutputPayload,
  RoomStatusChangedPayload,
  DeviceManager,
  MessageReceivedPayload,
  PeerJoinedPayload,
  PeerLeftPayload,
  Plugin,
  Room,
  uint8ArrayToValue,
  UserDataChangedPayload,
  valueToUint8Array,
  VideoInput,
  VideoOutputPayload,
  VAD_DEFAULTS,
  Transport,
  AudioInput,
  VideoOutput,
  JoinedPayload,
  InputSettings,
  ensurePlugin,
  setOutputDevice,
} from '@4players/odin';

import { config } from '../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  interval,
  map,
  Observable,
  pairwise,
  Subject,
  takeUntil,
} from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UserService } from './user.service';
import { AudioMuteStates, UserSettingsStore } from '../../state/user-settings.repository';
import { PermissionStore } from '../../state/permissions.repository';
import {
  createRoomResponseSchema,
  joinRoomResponseSchema,
  RoomErrorBody,
  TCreateRoomResponse,
  TJoinRoomResponse,
} from '../../shared/schemas/token';
import { RoomSchedule } from '../../shared/models/schedule';
import { ChatService } from './chat.service';
import { MessageTransfer } from '../../state/messages.repository';
import { sleep } from '../../shared/helpers/async';
import { isH3Supported, isTauri } from '../../shared/helpers/platform';
import { MediaConfigStore } from '../../state/media-config.repository';
import {
  DEFAULT_CAMERA_SETTINGS,
  DEFAULT_SCREEN_SETTINGS,
} from '../../shared/constants/video-settings';
import { getAllEntities, setEntitiesMap } from '@ngneat/elf-entities';
import { ParticipantSettingsStore } from '../../state/participant-settings.repository';
import { createPlugin } from '@4players/odin-plugin-web';
import {
  soundConnected,
  soundDisconnected,
  soundPeerJoined,
  soundPeerLeft,
  soundRecordingStarted,
  soundRecordingStopped,
} from '../../shared/constants/sounds';
import { AppConfigStore } from '../../state/app-config.repository';
import { RoomPasswordRejection, RoomPasswordStore } from '../../state/room-passwords.repository';
import { type CortexRoomFrame, parseCortexRoomFrame } from '../../shared/schemas/cortex-room-frame';
import { deriveRoomPasswordProof } from '../../shared/utils/room-password-proof';
import { deriveRoomMediaKey, roomMediaRoot } from '../../shared/utils/room-media-key';
import type { RoomPasswordProof } from '@andor/contracts/rooms';
import { normalizeRoomId } from '../../shared/utils/room-id';
import { TranslateService } from '@ngx-translate/core';
import { DeviceService } from './device.service';
import { LocalStreamService } from './local-stream.service';
import { MediaPermissionsService } from './media-permissions.service';
import { UserActivationService } from './user-activation.service';
import { ActiveSpeakerTrackerService } from './active-speaker-tracker.service';
import { AudioActivityService } from './audio-activity.service';
import { acquireRoomPresenceLock, RoomPresenceLock } from '../../shared/helpers/room-presence-lock';
import { RealtimeService } from './realtime.service';
import { ActivityLogService } from './activity-log.service';
import { AppService } from './app.service';
import { LanguageService } from './language.service';
import { formatDate } from '../../shared/i18n/date-format';

import { TokenGenerator, TokenOptions } from '@4players/odin-tokens';
import { Backend, error, log } from '@4players/odin-common';
import SetupCipherSettings = Backend.SetupCipherSettings;

// Fixed userId of the server-side transcription peer. When it joins, we
// surface it as a presence signal instead of adding it to the user list.
const TRANSCRIPTION_BOT_USER_ID = 'transcription-bot';

// A transcript frame is one utterance (at most 30 s of speech); anything far
// larger is not a frame the client wants to decode.
const MAX_CORTEX_FRAME_BYTES = 16 * 1024;

/**
 * The transcription bot joined or left the held room. `live` is true for a
 * join or leave while connected, false for the peer replay of a join or
 * reconnect (the bot was already there).
 */
export interface TranscriptionBotEvent {
  kind: 'joined' | 'left';
  roomId: string;
  peerId: number;
  live: boolean;
}

/** A Cortex Room Protocol frame the transcription bot sent into the held room. */
export interface CortexFrameEvent {
  roomId: string;
  botPeerId: number;
  frame: CortexRoomFrame;
}

/**
 * The media of the held room came back after a reconnect. `botPresent` is the
 * bot marker after the peer replay that preceded it.
 */
export interface MediaRejoinedEvent {
  roomId: string;
  botPresent: boolean;
}

/**
 * The user's answer to the recording-consent prompt while the transcription
 * bot is in the room: `null` = unanswered (the prompt is shown), `'given'` =
 * may transmit, `'dismissed'` = closed without consent (the mic stays gated
 * and the next unmute attempt re-opens the prompt).
 */
export type RecordingConsent = 'given' | 'dismissed' | null;

// How long after our own start-recording request a joining bot is still
// attributed to that request (the starter is auto-consented). Generous: the
// conference panel itself waits up to 10s for the bot to appear.
const OWN_RECORDING_ARM_TTL_MS = 30_000;

export enum OdinConnectionStatus {
  connecting,
  connected,
  reconnecting,
  disconnecting,
  disconnected,
}

export interface OdinConnectionError {
  reason: string;
  allowRetry?: boolean;
}

/**
 * Thrown by the join path when the server gates entry behind moderator approval
 * (HTTP 202 `APPROVAL_REQUIRED`). This is NOT a failure — `connect()` catches it
 * and routes to the pending-approval state instead of `_handleConnectError`.
 */
export class ApprovalRequiredError extends Error {
  constructor(
    readonly roomId: string,
    readonly requestId: string
  ) {
    super('approval required');
    this.name = 'ApprovalRequiredError';
  }
}

/**
 * Thrown by the join path when the server refuses a password-checked room
 * (HTTP 403 `ROOM_PASSWORD_INVALID`): `'required'` when we sent no password,
 * `'invalid'` when ours was wrong. Not a connection failure either — `connect()`
 * records it in `RoomPasswordStore` so the pre-join screens ask for the password.
 */
export class RoomPasswordError extends Error {
  constructor(readonly reason: RoomPasswordRejection) {
    super('room password rejected');
    this.name = 'RoomPasswordError';
  }
}

/**
 * The body of a 403 `ROOM_PASSWORD_INVALID` join refusal (carrying the room the
 * server resolved the join to), or `null` for any other error.
 */
function passwordRejection(e: unknown): { roomId?: string } | null {
  if (!(e instanceof HttpErrorResponse) || e.status !== 403) return null;
  const body = (e.error ?? {}) as RoomErrorBody;
  return body.code === 'ROOM_PASSWORD_INVALID' ? { roomId: body.roomId } : null;
}

/** The room the local user is waiting to be admitted to (knock-to-join). */
export interface PendingApproval {
  roomId: string;
  requestId: string;
}

/**
 * Injectable seam around `new Room()`. The SDK `Room` talks to real network
 * and media resources on join, so specs substitute a fake here to drive the
 * connection lifecycle (join, reconnecting, disconnect) without an SFU.
 */
@Injectable({
  providedIn: 'root',
})
export class OdinRoomFactory {
  create(): Room {
    return new Room();
  }
}

/**
 * How the held room's media transport is currently doing. Mirrors the ari
 * Room's own status while a session is held: `joining` (initial join or an
 * SDK-internal rejoin in flight), `joined`, or `reconnecting`. The app-level
 * `connecting`/`disconnecting` phases have no media equivalent — they are
 * transition bookends owned by OdinService.
 */
export type MediaConnectionState = 'joining' | 'joined' | 'reconnecting';

/**
 * The local user's claim on a room: "we joined `roomId` and intend to be in
 * it". Held from entering the room until an intentional teardown (disconnect,
 * connect failure, waiting for join approval). It deliberately SURVIVES media
 * trouble — the SDK room may die and reconnect while the session stands — and
 * is what the presence keepalive, the `room-rejoin` send, and the
 * `room-closed` teardown consult, so those cannot drift apart on connection
 * status. Owns the cross-tab presence lock (held for the whole session, auto-
 * released by the browser on tab close), so claim and lock end together.
 *
 * Within one session the media connection cycles: the SDK re-fires `onJoined`
 * after every internal reconnect. The session tracks that as the `media`
 * sub-state and owns the per-cycle stop subject, which resets on every
 * cycle. The transcription-bot marker is NOT cycle-scoped: it tracks the
 * bot's peer presence, which the SDK replays BEFORE `onJoined` fires, so it
 * must survive `markJoined()` and only clears when peers drop (reconnecting,
 * teardown) or the bot itself leaves.
 *
 * Recording consent is session-scoped as well: it survives SDK reconnects
 * (the bot marker flips null → non-null there, but the user already answered)
 * and resets only when the bot really leaves (see `_onPeerLeft` /
 * `_onRoomJoined`) or when a fresh session is created by an active (re)join.
 */
class RoomSession {
  constructor(
    readonly roomId: string,
    private readonly _lock: RoomPresenceLock | null
  ) {}

  private readonly _media = signal<MediaConnectionState>('joining');
  /** The media sub-state of this session (see {@link MediaConnectionState}). */
  readonly media = this._media.asReadonly();

  private _mediaStop$ = new Subject<null>();
  /**
   * Completes when the CURRENT media cycle ends (reconnect or session
   * teardown) — scope per-cycle subscriptions with `takeUntil(mediaStop$)`.
   * Read it AFTER `markJoined()` so it refers to the fresh cycle.
   */
  get mediaStop$(): Observable<null> {
    return this._mediaStop$.asObservable();
  }

  /** The transcription-bot peer currently in the room, if present. */
  readonly transcriptionBot = signal<{ peerId: number } | null>(null);

  /** Whether the media joined at least once, so the next join is a rejoin. */
  hasJoined = false;

  /**
   * The media key epoch the room's cipher is keyed for, or `null` in an
   * unencrypted room (no cipher). Only ever moves forward.
   */
  readonly mediaKeyEpoch = signal<number | null>(null);

  /** The user's answer to the recording-consent prompt (see {@link RecordingConsent}). */
  readonly recordingConsent = signal<RecordingConsent>(null);
  /**
   * The mic was live when the consent gate auto-muted it (bot appeared while
   * unmuted, joined unmuted, or an unmute attempt) — restored on grant.
   */
  micIntentBeforeConsent = false;
  /**
   * Epoch-ms until which the next bot join is attributed to OUR start-recording
   * request. Armed before the HTTP request goes out (the bot can reach the SFU
   * before the reply arrives), consumed by the bot's onPeerJoined, and expires
   * on its own so a stale arm never auto-consents someone else's recording.
   */
  private _ownRecordingArmedUntil = 0;

  armOwnRecording(ttlMs = OWN_RECORDING_ARM_TTL_MS): void {
    this._ownRecordingArmedUntil = Date.now() + ttlMs;
  }

  disarmOwnRecording(): void {
    this._ownRecordingArmedUntil = 0;
  }

  /** Whether the bot joining right now is ours; one-shot. */
  consumeOwnRecording(): boolean {
    const armed = Date.now() < this._ownRecordingArmedUntil;
    this._ownRecordingArmedUntil = 0;
    return armed;
  }

  /** The recording ended (or never concerned this session): ask again next time. */
  resetRecordingConsent(): void {
    this.recordingConsent.set(null);
    this.micIntentBeforeConsent = false;
    this._ownRecordingArmedUntil = 0;
  }

  /** The media (re)connected: start a fresh cycle and mark it joined. */
  markJoined(): void {
    // Defensive cycle end: onJoined re-fires on every SDK-internal reconnect,
    // and the reconnecting transition usually ended the cycle already. The
    // bot marker must NOT be cleared here: the SDK replays existing peers
    // before onJoined (initial join and reconnect alike), so the marker
    // already belongs to the fresh cycle — a bot that started recording
    // before we joined was just detected from that replay.
    this.endMediaCycle();
    this._mediaStop$ = new Subject<null>();
    this._media.set('joined');
    this.hasJoined = true;
  }

  /** The media transport dropped; the SDK is trying to get it back. */
  markReconnecting(): void {
    this.endMediaCycle();
    // All remote peers (bot included) are gone with the transport; the
    // replay before the next onJoined re-detects a still-present bot.
    this.transcriptionBot.set(null);
    this._media.set('reconnecting');
  }

  /**
   * End the current media cycle: complete the per-cycle stop subject.
   * Idempotent.
   */
  endMediaCycle(): void {
    this._mediaStop$.next(null);
    this._mediaStop$.complete();
  }

  /**
   * End the session: close the media cycle, clear the bot marker, and
   * release the cross-tab presence lock (idempotent).
   */
  release(): void {
    this.endMediaCycle();
    this.transcriptionBot.set(null);
    this._lock?.release();
  }
}

@Injectable({
  providedIn: 'root',
})
export class OdinService {
  private readonly roomsApi = inject(RoomsApi);
  private readonly _streamStats = inject(StreamStatsService);
  private readonly _language = inject(LanguageService);
  private _plugin?: Plugin;
  private _pluginInit?: Promise<void>;
  private _odinRoom = new BehaviorSubject<Room | null>(null);
  odinRoom$ = this._odinRoom.asObservable();
  private _audioInput = new BehaviorSubject<AudioInput | null>(null);
  audioInput$ = this._audioInput.asObservable();
  // Signal-based view used by templates rendered per-row (e.g. user-avatar).
  // Avoids N async-pipe subscriptions to the same BehaviorSubject during
  // virtual-scroll churn — one shared signal instead.
  hasAudioInput: Signal<boolean> = toSignal(this._audioInput.pipe(map((v) => v !== null)), {
    initialValue: false,
  });

  get audioInput() {
    return this._audioInput.value;
  }

  // The connect/disconnect transition bookends. These app-level phases have
  // no media-substate equivalent: `connecting` spans token fetch + lock +
  // join-in-flight BEFORE a session exists, `disconnecting` spans an
  // intentional teardown WHILE the session still exists.
  private readonly _connectionTransition = signal<'none' | 'connecting' | 'disconnecting'>('none');

  /**
   * The connection status, derived from the session + its media sub-state +
   * the transition bookends (row order = precedence):
   *
   * | transition      | session  | media()      | derived       |
   * |-----------------|----------|--------------|---------------|
   * | `disconnecting` | any      | any          | disconnecting |
   * | else            | non-null | joined       | connected     |
   * | else            | non-null | reconnecting | reconnecting  |
   * | else            | non-null | joining      | connecting    |
   * | `connecting`    | null     | —            | connecting    |
   * | `none`          | null     | —            | disconnected  |
   *
   * `disconnecting` beats a live session so the peer-event gates stay closed
   * during `leave()`'s teardown storm; a joined session beats `connecting`
   * so a refused/duplicate connect() never flickers the status.
   */
  private readonly _derivedStatus: Signal<OdinConnectionStatus> = computed(() => {
    if (this._connectionTransition() === 'disconnecting') {
      return OdinConnectionStatus.disconnecting;
    }
    const session = this._session();
    if (session) {
      switch (session.media()) {
        case 'joined':
          return OdinConnectionStatus.connected;
        case 'reconnecting':
          return OdinConnectionStatus.reconnecting;
        case 'joining':
          return OdinConnectionStatus.connecting;
      }
    }
    return this._connectionTransition() === 'connecting'
      ? OdinConnectionStatus.connecting
      : OdinConnectionStatus.disconnected;
  });

  /** The connection status (see {@link _derivedStatus} for the derivation). */
  readonly status: Signal<OdinConnectionStatus> = this._derivedStatus;

  private _connectionError = signal<OdinConnectionError | null>(null);
  connectionError = this._connectionError.asReadonly();

  // Set when a join is gated behind moderator approval (HTTP 202). The pre-join
  // UI renders a "waiting for a moderator" overlay while this is non-null; it is
  // cleared on approval (which re-joins), denial, expiry, or user cancel.
  private _pendingApproval = signal<PendingApproval | null>(null);
  readonly pendingApproval = this._pendingApproval.asReadonly();
  private _isAudioStarting = signal(false);
  isAudioStarting = this._isAudioStarting.asReadonly();
  // In-flight dedupe latch for ensureAudioInputExists() — concurrent callers
  // await the same creation attempt. Unrelated to the `isAudioStarting` UI
  // signal above, hence the distinct name.
  private _audioInputCreationInFlight: Promise<boolean> | null = null;
  // The held room claim (see RoomSession). Non-null from entering a room
  // until disconnect / connect failure / awaiting join approval.
  private readonly _session = signal<RoomSession | null>(null);
  // Presence of the transcription bot peer of the current media cycle.
  // Non-null while the bot is in the room — used by the UI to indicate the
  // session is being recorded and transcribed. The bot itself is filtered
  // out of the user store. Owned by the session; null when no room is held.
  readonly transcriptionBot: Signal<{ peerId: number } | null> = computed(
    () => this._session()?.transcriptionBot() ?? null
  );
  private readonly _transcriptionBotEvents = new Subject<TranscriptionBotEvent>();
  /** The bot's joins and leaves in the held room; see {@link TranscriptionBotEvent}. */
  readonly transcriptionBotEvents$: Observable<TranscriptionBotEvent> =
    this._transcriptionBotEvents.asObservable();
  // A Subject, not a signal: a burst of frames must not collapse into one.
  private readonly _cortexFrames = new Subject<CortexFrameEvent>();
  /**
   * The transcription bot's room frames, decoded and validated as Cortex Room
   * Protocol envelopes. Live transcript lines ride on them; they may miss
   * lines (joins, reconnects, key switches), so the backlog stays the source
   * of truth.
   */
  readonly cortexFrames$: Observable<CortexFrameEvent> = this._cortexFrames.asObservable();
  private readonly _mediaRejoined = new Subject<MediaRejoinedEvent>();
  /** The held room's media rejoined after a reconnect; see {@link MediaRejoinedEvent}. */
  readonly mediaRejoined$: Observable<MediaRejoinedEvent> = this._mediaRejoined.asObservable();
  /**
   * The held room's media key epoch, or `null` when no encrypted room is held.
   * See {@link applyMediaKeyEpoch}.
   */
  readonly mediaKeyEpoch: Signal<number | null> = computed(
    () => this._session()?.mediaKeyEpoch() ?? null
  );
  // The latest `room-media-key` epoch for a room we are still joining: the
  // session (and its cipher) does not exist yet, so it is applied right after
  // the join. Reset by every connect.
  private _pendingMediaKeyEpoch: { roomId: string; epoch: number } | null = null;
  // mutedState as a signal, for the recording-consent and browser-access
  // effects in the constructor (the elf store is observable-first).
  private readonly _mutedState: Signal<AudioMuteStates> = toSignal(
    this._userSettingsStore.mutedState$,
    { initialValue: this._userSettingsStore.value.mutedState }
  );
  /**
   * The transcription bot is in the room and the user has not consented to
   * being recorded: the mic must not transmit. Drives the reconcile gate and
   * the auto-mute effect.
   */
  readonly recordingConsentRequired: Signal<boolean> = computed(() => {
    const session = this._session();
    return (
      !!session && session.transcriptionBot() !== null && session.recordingConsent() !== 'given'
    );
  });
  /** The bot is in the room and the consent question is unanswered: show the dialog. */
  readonly recordingConsentPromptVisible: Signal<boolean> = computed(() => {
    const session = this._session();
    return !!session && session.transcriptionBot() !== null && session.recordingConsent() === null;
  });

  get room(): Room | null {
    return this._odinRoom.value;
  }

  /** The id of the room we actually joined; `null` when not connected. */
  get currentRoomId(): string | null {
    return this._session()?.roomId ?? null;
  }

  /** Reactive {@link currentRoomId}: the joined room's id, `null` when not in a room. */
  readonly joinedRoomId: Signal<string | null> = computed(() => this._session()?.roomId ?? null);

  /**
   * "I understand": recording consent for this session. A mic the consent gate
   * auto-muted (or an unmute attempt it intercepted) is restored right away.
   */
  grantRecordingConsent(): void {
    const session = this._session();
    if (!session) return;
    session.recordingConsent.set('given');
    if (session.micIntentBeforeConsent) {
      session.micIntentBeforeConsent = false;
      this._userSettingsStore.update({ mutedState: AudioMuteStates.none });
    }
  }

  /** "Stay muted": close the prompt without consent; the mic stays gated. */
  dismissRecordingConsent(): void {
    this._session()?.recordingConsent.set('dismissed');
  }

  /**
   * Unmute pre-check for UI writers. When consent is required it records the
   * mic intent, (re)opens the prompt and returns `true` — the caller must then
   * NOT write `mutedState: none`. (The consent effect would revert such a
   * write anyway, but a transient unmute burns a one-shot talk grant.)
   */
  requestRecordingConsent(): boolean {
    const session = this._session();
    if (!session || !this.recordingConsentRequired()) return false;
    session.micIntentBeforeConsent = true;
    if (session.recordingConsent() === 'dismissed') {
      session.recordingConsent.set(null);
    }
    return true;
  }

  /** Our own start-recording request is going out: the next bot join is ours (no prompt). */
  markOwnRecordingStart(): void {
    this._session()?.armOwnRecording();
  }

  /** The start-recording request failed: a bot joining now is someone else's. */
  clearOwnRecordingStart(): void {
    this._session()?.disarmOwnRecording();
  }

  constructor(
    private userService: UserService,
    private chatService: ChatService,
    private translateService: TranslateService,
    private appStore: AppStore,
    private usersStore: UserStore,
    private _mediaConfigStore: MediaConfigStore,
    private _userSettingsStore: UserSettingsStore,
    private _participantSettingsStore: ParticipantSettingsStore,
    private _appConfigStore: AppConfigStore,
    private _deviceService: DeviceService,
    private _localStream: LocalStreamService,
    private _permissions: MediaPermissionsService,
    private _userActivation: UserActivationService,
    private _activeSpeakerTracker: ActiveSpeakerTrackerService,
    private _audioActivity: AudioActivityService,
    private _realtime: RealtimeService,
    private _activityLog: ActivityLogService,
    private _appService: AppService,
    private _permissionStore: PermissionStore,
    private _roomPasswords: RoomPasswordStore,
    private _roomFactory: OdinRoomFactory
  ) {
    // The notifications WS reached OPEN (fires on initial connect and after
    // every reconnect — deliberately NOT onReconnect$, which fires while the
    // replacement socket is still being built, so a send there is silently
    // dropped by the channel). If we still believe we're in a room, the server
    // may have aged us out of its active-room registry during the gap —
    // re-register without taking a new token (the current ODIN token is still
    // valid). Guarded by the session alone, not connection status: the
    // session is the single source of truth for "we hold a room", it covers
    // the media-`reconnecting` and join-in-flight windows, and the server
    // treats a rejoin for a missing room as a no-op, so an extra send never
    // hurts.
    this._realtime.onConnected$.subscribe(() => {
      const session = this._session();
      if (session) {
        this._realtime.send({ type: 'room-rejoin', roomId: session.roomId });
      }
    });

    // Keep the server's active-room registry aware that we're still here.
    // Server timeout is 60s; pinging at 30s gives a one-miss safety margin.
    // Armed once for the service lifetime and guarded by the session instead
    // of the SDK join lifecycle: the media teardown runs on every
    // `reconnecting` transition, and a keepalive scoped to the media cycle
    // (`mediaStop$`) would silently die there, starving our presence while
    // the connection recovers. The server ignores pings for a room that no
    // longer exists, so a stale ping can never resurrect one.
    interval(30_000).subscribe(() => {
      const session = this._session();
      if (session) {
        this._realtime.send({ type: 'room-active', roomId: session.roomId });
      }
    });

    // Knock-to-join: outcomes for a join we're waiting on. The notifications WS
    // is connected at app init, so these arrive while the user waits at the
    // pre-join screen (Room Login / Preparation Area). Also the encrypted
    // room's media key rotations.
    this._realtime.serverEvents$.subscribe((event) => {
      const pending = this._pendingApproval();
      switch (event.type) {
        case 'join-approved':
          if (pending && pending.roomId === event.data.roomId) {
            // Grant recorded server-side; re-join consumes it and mints a token.
            this._pendingApproval.set(null);
            void this.connect(event.data.roomId);
          }
          break;
        case 'join-denied':
          if (pending && pending.roomId === event.data.roomId) {
            this._pendingApproval.set(null);
            this._connectionError.set({
              allowRetry: false,
              reason: this.translateService.instant('A moderator declined your request to join.'),
            });
          }
          break;
        case 'join-request-expired':
          if (pending && pending.roomId === event.data.roomId) {
            this._pendingApproval.set(null);
            this._connectionError.set({
              allowRetry: true,
              reason: this.translateService.instant('No moderator responded. You can try again.'),
            });
          }
          break;
        case 'room-media-key':
          this.applyMediaKeyEpoch(event.data.roomId, event.data.epoch);
          break;
      }
    });
    // Cleanup when the renderer unloads. Mobile WebViews never fire
    // `unload`; `pagehide` covers reloads and closing there. The browser
    // keeps `unload`, which also keeps the page out of the back/forward cache.
    window.addEventListener(isTauri ? 'pagehide' : 'unload', () => {
      void this.disconnect(undefined, true);
    });

    // Reacts to the current appState and auto connects the app
    this.appStore.isConnectedView$.subscribe(async (isConnectedView) => {
      if (isConnectedView) {
        if (this.isConnecting() || this._session()) return;
        // Wait for `AppService.init()` so the backend-driven `developerMode`
        // is loaded before `connect()` mints its token. On a
        // `persistAppState` reload this subscription replays synchronously
        // during DI — well before `init()` runs — so without this gate the
        // join would see the default `developerMode: false` and skip the
        // developer token path.
        await this._appService.whenInitialized();
        // Re-check guards: the user could have disconnected, or another
        // connect could have started, while we were awaiting init.
        if (!this._appService.isReady() || !this.appStore.value.isConnectedView) return;
        if (this.isConnecting() || this._session()) return;
        const success = await this.connect(this._userSettingsStore.value.roomId);
        if (!success) {
          // `connect()` already recorded the failure (connectionError +
          // `disconnected` status via `_handleConnectError`). That status makes
          // `disconnect()` a no-op here — its `isDisconnecting()` guard trips on
          // `disconnected` — so it would never clear the persisted
          // `isConnectedView: true`, leaving an empty conference shell. Drop the
          // optimistic connected view and fall back to the pre-join screen,
          // which renders the preserved error.
          this._appService.showFailedConnectFallback();
        }
      } else {
        // A held session means we're (still) in a room — leave it. Reentrant
        // no-op during disconnect(): the session is already null by the time
        // the isConnectedView flip re-fires this subscription.
        if (this._session()) {
          await this.disconnect();
        }
      }
    });

    // A room can be closed/deleted server-side while we're in it (e.g. a
    // moderator deletes it). Everyone on the domain gets the broadcast, but only
    // the user actually connected to that room tears down and returns to the
    // pre-join screen (preparation-area if invited, room-login otherwise).
    this._appService.roomClosed$.subscribe(async (closedRoomId) => {
      if (this._session()?.roomId !== closedRoomId) return;
      await this.disconnect({
        allowRetry: false,
        reason: this.translateService.instant('This room was closed.'),
      });
      // `disconnect()` records the connectionError; this routes to the right
      // pre-join view (by durable join origin, since `wasInvited` is cleared on
      // join) without clobbering it.
      this._appService.showRemovedFromRoomFallback();
    });

    // Update the AudioInput with the latest device. Dedupe by id: a
    // label-only refresh of the default entry must not re-open the capture —
    // the plugin follows the OS default itself.
    this._deviceService.activeAudioInputDevice$
      .pipe(distinctUntilChanged((prev, curr) => prev?.id === curr?.id))
      .subscribe(async (device) => {
        if (this.audioInput) {
          await this.audioInput.setDevice({
            device,
            // The plugin REPLACES (not merges) this callback on every
            // setDevice — it must be passed here and in createAudioInput
            // alike, or the status feed silently stops.
            onStatusChanged: (status) => this._onCaptureStatusChanged(status),
          });
        }
      });

    // Update the AudioOutput, deduped by id like the input above
    this._deviceService.activeAudioOutputDevice$
      .pipe(distinctUntilChanged((prev, curr) => prev?.id === curr?.id))
      .subscribe(async (device) => {
        if (this._odinRoom.getValue()?.status.status === 'joined') {
          setOutputDevice({ device });
        }
      });

    // Stop the cam if the stream is stopped
    this._localStream.camStream$.subscribe(async (ms) => {
      if (!ms) {
        this.stopCam();
      }
    });

    // Start the screen cast if the stream is started
    this._localStream.screenCastStream$.subscribe(async (ms) => {
      if (ms) {
        // @TODO Check if its fine to directly call startScreenCast or listen to this event.
        await this.startScreenCast(ms);
      } else {
        this.stopScreenCast();
      }
    });

    // mutedState only controls room playback volume here. Mic creation,
    // attach/detach, and capture mute live in the reconcile subscription below.
    this._userSettingsStore.mutedState$.subscribe((state) => {
      const roomVolume = this._userSettingsStore.value.roomVolume;
      switch (state) {
        case AudioMuteStates.none:
        case AudioMuteStates.mic:
          this.room?.setVolume(roomVolume);
          if (
            state === AudioMuteStates.none &&
            this._userSettingsStore.value.talkPermissionGranted &&
            // A transient `none` the recording-consent effect is about to
            // revert must not burn the one-shot grant before the user ever
            // transmitted.
            !untracked(this.recordingConsentRequired)
          ) {
            this._userSettingsStore.update({ talkPermissionGranted: false });
          }
          break;
        case AudioMuteStates.speakers:
        case AudioMuteStates.speakersKeepMic:
          this.room?.setVolume(0);
          break;
      }
    });

    // Privilege revocation force-mute: when the effective role loses
    // `useMicrophone` while the mic is live, mute client-side; the reconcile
    // subscription below then detaches the AudioInput. Keyed on the privilege
    // TRANSITION (true -> false), not on the combined no-permission predicate:
    // the one-shot talk grant is consumed the instant the user goes mic-active
    // (see the mutedState$ subscription above), so a predicate-based guard
    // would instantly re-mute a legitimately talking granted user. Elf's
    // select() dedupes, so pairwise sees real changes only; the store boots at
    // `true` (schema default, not persisted), so a role lacking the privilege
    // always produces this transition after load — which also catches
    // persisted-unmuted session restores.
    this._permissionStore.useMicrophone$.pipe(pairwise()).subscribe(([had, has]) => {
      if (had && !has && this._userSettingsStore.value.mutedState === AudioMuteStates.none) {
        this._userSettingsStore.update({ mutedState: AudioMuteStates.mic });
      }
    });

    // Recording-consent force-mute: while the transcription bot is in the room
    // and consent is not given, an active mic — bot appeared while unmuted,
    // joined unmuted, or any unmute path (dock, speaker toggle, talk grant,
    // persisted-unmuted reload) — is muted so the UI and peers see the truth;
    // the intent is remembered so "I understand" restores it, and a dismissed
    // prompt is re-opened. Deliberately an effect rather than a mutedState$
    // subscriber: a same-prop revert inside the store emission is reentrant
    // and can leave later subscribers on the stale value. The reconcile below
    // independently refuses to attach while consent is required, so no audio
    // reaches the room in the tick before this runs.
    effect(() => {
      if (!this.recordingConsentRequired()) return;
      if (this._mutedState() !== AudioMuteStates.none) return;
      untracked(() => {
        const session = this._session();
        if (!session) return;
        session.micIntentBeforeConsent = true;
        if (session.recordingConsent() === 'dismissed') {
          session.recordingConsent.set(null);
        }
        this._userSettingsStore.update({ mutedState: AudioMuteStates.mic });
      });
    });

    // Browser mic access enforcement, mirroring the camera's in
    // LocalStreamService. Revoked (reset or blocked in the site settings while
    // granted): the capture is dead — the plugin neither notices the ended
    // track nor re-opens it — so mute and drop the AudioInput; the next unmute
    // then re-runs ensureAudioInputExists() → requestMic(), re-showing the
    // browser prompt like the first unmute. Mute BEFORE dropping: an input-less
    // reconcile that still wants capture would prompt right away. Blocked: an
    // unmute from any path (speaker toggle, talk grant, consent restore,
    // persisted state) can never capture and the browser won't prompt, so it
    // is reverted; the dock opens the permission guide for its own toggle. An
    // effect for the same reentrancy reason as the consent effect above.
    let prevMicAccess = untracked(this._permissions.microphoneAccess);
    effect(() => {
      const micAccess = this._permissions.microphoneAccess();
      const revoked = prevMicAccess === 'granted' && micAccess !== 'granted';
      prevMicAccess = micAccess;
      const blockedUnmute = micAccess === 'denied' && this._mutedState() === AudioMuteStates.none;
      if (!revoked && !blockedUnmute) return;
      untracked(() => {
        if (this._userSettingsStore.value.mutedState === AudioMuteStates.none) {
          this._userSettingsStore.update({ mutedState: AudioMuteStates.mic });
        }
        if (revoked) this._dropAudioInput();
      });
    });

    // Same enforcement for video: `useCamera` gates both the camera and the
    // screen share, so a revocation stops whichever is live. Stopping the
    // device streams is enough — the screenCastStream$ subscription above and
    // the camera handling cascade the room-side detach. Video has no
    // one-shot-grant escape hatch, so no further guards are needed.
    this._permissionStore.useCamera$.pipe(pairwise()).subscribe(([had, has]) => {
      if (had && !has) {
        if (this._localStream.camStream) {
          this._localStream.stopCamStream('useCamera privilege revoked');
        }
        if (this._localStream.screenCastStream) {
          this._localStream.stopScreenCastStream();
        }
      }
    });

    // Single reconcile loop owning mic attach/detach + capture volume. Driven
    // by the tuple of (audioInput, room, mutedState, microphoneAccess,
    // activeSection, media sub-state, recording consent) — when any of those
    // change, this fires and reconciles the SDK to match intent. Lazily kicks
    // off audioInput creation when capture is wanted but no AudioInput exists
    // yet; the resulting `_audioInput.next(...)` re-triggers this same
    // subscription, which then performs the attach.
    //
    // The mic may run WITHOUT a room only on surfaces that display a live mic
    // meter (the mic test) — the preparation area and the audio settings.
    // Everywhere else roomless (e.g. the room-login screen after a disconnect)
    // there is no reason to hold the mic, so it is fully released
    // (`setVolume('muted')`), which stops the underlying MediaStream in the SDK.
    // Without this gate the mic would stay captured after leaving a room, since
    // `mutedState` is still `none` on disconnect.
    const mediaState = computed(() => this._session()?.media() ?? null);
    combineLatest([
      this._audioInput,
      this._odinRoom,
      this._userSettingsStore.mutedState$,
      toObservable(this._permissions.microphoneAccess),
      this.appStore.activeSection$,
      // Re-triggers only (joining → joined / reconnecting; bot or consent
      // changes) — their values are read from the signals below.
      toObservable(mediaState),
      toObservable(this.recordingConsentRequired),
    ]).subscribe(async ([input, room, muted, micAccess, section]) => {
      // Signal-backed inputs are read directly rather than from the tuple: the
      // elf store emits synchronously while toObservable emits a tick later,
      // so the tuple's copy can be stale on a mutedState flip.
      const media = untracked(mediaState);
      const consentRequired = untracked(this.recordingConsentRequired);
      const userWantsMicActive = muted === AudioMuteStates.none;
      const onMicTestSurface = section === 'preparation-area' || section === 'general-settings';
      // The mic never attaches before the initial join completes: the SDK
      // replays the peers already in the room (transcription bot included)
      // BEFORE onJoined, so once media is 'joined' we know whether recording
      // consent is required. 'reconnecting' keeps the attach as today — the
      // SDK re-establishes it; a detach/re-attach here would only add a gap.
      const pendingJoin = !!room && media === 'joining';
      // Capture when the user wants the mic AND there is somewhere for it to go
      // — a joined room to transmit into, or a mic-test surface to meter — AND
      // the room's recording (if any) has been consented to.
      const hasDestination = (!!room && !pendingJoin) || onMicTestSurface;
      const wantCapture = userWantsMicActive && hasDestination && !consentRequired;

      // Creation is gated on `wantCapture` — ensureAudioInputExists() calls
      // requestMic() internally, which is what surfaces the browser prompt when
      // micAccess is 'prompt'. Skipping when 'denied' avoids re-prompting a user
      // who has already refused. If the user denies, demote mutedState to mic so
      // the UI reflects reality.
      if (wantCapture && !input && micAccess !== 'denied') {
        this.ensureAudioInputExists()
          .then((ok) => {
            if (!ok && this._userSettingsStore.value.mutedState === AudioMuteStates.none) {
              this._userSettingsStore.update({ mutedState: AudioMuteStates.mic });
            }
          })
          .catch(console.error);
        return;
      }
      if (!input) return;

      // Attach + capture is additionally gated on granted permission.
      const shouldCapture = wantCapture && micAccess === 'granted';
      try {
        // After each await: the input may have been dropped meanwhile (browser
        // access revoked) — never re-attach or re-detach a closed input.
        if (shouldCapture) {
          await input.setVolume(this._userSettingsStore.value.inputVolume);
          if (input !== this._audioInput.value) return;
          if (room) await room.addAudioInput(input);
        } else {
          // No room ⇒ nothing to keep warm for ⇒ fully release the mic
          // ('muted') so the SDK stops the MediaStream. Connected-but-muted ⇒
          // honor stopMicAccessOnMute (0 keeps the stream warm for fast unmute).
          // A pending join with an active mic always keeps the stream warm, so
          // a mic that was live in the preparation area doesn't stop and
          // re-open across the join.
          const keepWarm = pendingJoin && userWantsMicActive;
          const releaseMic =
            !room || (this._userSettingsStore.value.stopMicAccessOnMute && !keepWarm);
          await input.setVolume(releaseMic ? 'muted' : 0);
          if (input !== this._audioInput.value) return;
          // (Nothing is attached yet during a pending join.)
          if (room && !pendingJoin) room.removeAudioInput(input);
        }
      } catch (e) {
        console.warn(e);
      }
    });

    // Master volume slider
    this._userSettingsStore.roomVolume$.subscribe(async (volume) => {
      const room = this._odinRoom.value;
      if (!room) return;
      room.setVolume([volume, volume]);
    });

    // Input volume. Mirror the reconcile's surface gate so a roomless volume
    // change (e.g. replayed persisted state) can't re-acquire a released mic:
    // only apply when connected or on a mic-test surface.
    this._userSettingsStore.inputVolume$.subscribe(async (volume) => {
      const audioInput = this.audioInput;
      if (!audioInput || this._userSettingsStore.value.mutedState !== AudioMuteStates.none) return;
      const section = this.appStore.value.activeSection;
      const onMicTestSurface = section === 'preparation-area' || section === 'general-settings';
      if (!this.room && !onMicTestSurface) return;
      await audioInput.setVolume(volume);
    });

    // Echo cancellation
    this._mediaConfigStore.echoCancellation$.subscribe(async (value) => {
      const audioInput = this.audioInput;
      if (audioInput) {
        await audioInput.setInputSettings({ echoCanceller: value });
      }
    });

    // Auto gain control
    this._mediaConfigStore.autoGainControl$.subscribe(async (value) => {
      const audioInput = this.audioInput;
      if (audioInput) {
        await audioInput.setInputSettings({ gainController: value });
      }
    });

    // Noise suppression
    this._mediaConfigStore.noiseSuppression$.subscribe(async (value) => {
      const audioInput = this.audioInput;
      if (audioInput) {
        await audioInput.setInputSettings({ noiseSuppression: value });
      }
    });

    // Voice activity detection
    this._userSettingsStore.voiceActivityDetection$.subscribe(async (value) => {
      const audioInput = this.audioInput;
      if (audioInput) {
        await audioInput.setInputSettings({
          voiceActivity: this.voiceActivityFromBool(value),
        });
      }
    });

    // Volume Gate
    this._userSettingsStore.volumeGate$.subscribe(async (value) => {
      const audioInput = this.audioInput;
      if (audioInput) {
        await audioInput.setInputSettings({
          volumeGate: this.voiceActivityFromValue(value),
        });
      }
    });

    // Mute/Unmutes everybody that updates his breakout room id
    this.usersStore.breakoutRoomChanges$.subscribe((changes) => {
      // In the case, I removed the AudioOutput from a user from the room and now, changed my breakout room id, I need to add the AudioOutput back to the room.
      changes.forEach(({ user }) => {
        if (user.isSelf) {
          // Mute everybody that is not in the same breakout room
          this.usersStore.store.query(getAllEntities()).forEach((u) => {
            if (u.data.breakoutRoomId !== user.data.breakoutRoomId) {
              if (u.audioOutput) {
                this.room?.removeAudioOutput(u.audioOutput);
                //u.audioOutput?.pause();
              }
            } else {
              if (u.audioOutput) {
                this.room?.addAudioOutput(u.audioOutput);
                //u.audioOutput?.resume();
              }
            }
          });
          return;
        }
        // Currently, even if users are already muted, they will be muted again
        if (user.data.breakoutRoomId !== this.userService.ownUser()?.data.breakoutRoomId) {
          if (user.audioOutput) {
            this.room?.removeAudioOutput(user.audioOutput);
            //user.audioOutput?.pause();
          }
        } else {
          if (user.audioOutput) {
            this.room?.addAudioOutput(user.audioOutput);
            //user.audioOutput?.resume();
          }
        }
      });
    });
  }

  /**
   * Keeps the microphone level (`PowerLevel`) live for a meter. The native
   * plugin polls the level only while someone holds such a poll — speaking
   * state alone is event-driven — so every meter holds one for its lifetime.
   * A no-op with the web plugin, which reads levels in-process. Returns the
   * release.
   */
  holdLevelPolling(intervalMs = 50): () => void {
    type LevelPolling = {
      acquirePropsPolling?: (intervalMs: number) => void;
      releasePropsPolling?: (intervalMs: number) => void;
    };
    if (!isTauri) return () => {};
    let released = false;
    let held: LevelPolling | undefined;
    // main.ts registers the native plugin before bootstrap.
    ensurePlugin()
      .then((plugin) => {
        if (released) return;
        held = plugin as LevelPolling;
        held.acquirePropsPolling?.(intervalMs);
      })
      .catch((e) => error('failed to start level polling', { e }));
    return () => {
      if (released) return;
      released = true;
      held?.releasePropsPolling?.(intervalMs);
    };
  }

  init(): Promise<void> {
    // Memoized: a second call must not try to register another plugin.
    return (this._pluginInit ??= this._registerPlugin());
  }

  private async _registerPlugin(): Promise<void> {
    if (isTauri) {
      // Native audio/video backend: capture, playback and transport run in
      // Rust; the SDK classes above stay the same. main.ts registers it
      // before bootstrap, so this only picks up the registered instance.
      this._plugin = await ensurePlugin();
      return;
    }
    const plugin = (this._plugin = createPlugin(
      async (sampleRate) => {
        const audioContext = new AudioContext({ sampleRate });
        await audioContext.resume();
        return audioContext;
      },
      {
        onLogMessage: (formattedMessage, record) => {
          if (config.exposeDevTools) {
            console.log(formattedMessage);
          }
        },
      }
    ));
    // The web plugin's room holds the RTCPeerConnection stats, but the SDK
    // keeps it private: hand every room the plugin creates (reconnects
    // included) to the stream stats.
    const joinRoom = plugin.joinRoom.bind(plugin);
    plugin.joinRoom = (parameters) => {
      const webRoom = joinRoom(parameters);
      this._streamStats.registerWebStatsSource(() => webRoom.getRtcStats());
      return webRoom;
    };
    // `.catch()` needs a handler — without one the rejection propagates to
    // the derived promise and surfaces as an unhandled rejection.
    ensurePlugin(plugin).catch((e) => error('failed to register ODIN plugin', { e }));
  }

  resetConnectionError() {
    this._connectionError.set(null);
  }

  voiceActivityFromBool(value: boolean): InputSettings['voiceActivity'] {
    if (value) {
      return VAD_DEFAULTS.voiceActivity;
    }
    return false;
  }

  voiceActivityFromValue(value: number): InputSettings['volumeGate'] {
    if (Math.abs(-90.0 - value) >= Number.EPSILON) {
      return value;
    }
    return false;
  }

  /**
   * Initializes and joins a room.
   *
   * `isPrivate` is forwarded to the server's active-room registry, set
   * when the room is first created on the server and immutable
   * afterwards — subsequent joiners' values are ignored. Defaults to
   * false (public).
   *
   * The cipher password is sourced from `RoomPasswordStore` keyed by
   * `roomId`. Callers wanting end-to-end encryption must write the
   * password to the store before invoking `connect()` (either typed in by
   * the user, generated locally for a brand-new room, or recovered from
   * the invite-link fragment). If no password is registered for the room
   * the cipher is omitted, the room is unencrypted, and `hasPassword` is
   * reported to the server as false.
   */
  async connect(
    roomId: string,
    enforceWebrtcTransport?: boolean,
    joinWithCam: boolean = false,
    opts?: { createIfMissing?: boolean; roomName?: string }
  ): Promise<boolean> {
    const heldSession = this._session();
    // A held session for a DIFFERENT room: refuse. Proceeding would silently
    // orphan the current Room and its cross-tab presence lock — switching
    // rooms is an explicit disconnect-then-connect in this app. The joined
    // session outranks the transition in the derived status, so refusing
    // never flickers the visible status.
    if (heldSession && heldSession.roomId !== roomId) {
      this._connectionError.set({
        allowRetry: false,
        reason: this.translateService.instant(
          'Unable to join the room. Please leave the current room first.'
        ),
      });
      return false;
    }
    this._connectionError.set(null);
    this._connectionTransition.set('connecting');
    this._pendingMediaKeyEpoch = null;
    if (!this._plugin) {
      await this.init();
    }
    // Avoid connecting to the same room twice
    if (heldSession?.roomId === roomId) {
      // Already connected to this room
      if (heldSession.media() === 'joined') {
        this._connectionTransition.set('none');
        return true;
      } else {
        this.disconnectWithLog(
          this.translateService.instant(
            'Unable to join the room. An error occurred while trying to connect. Please try again.'
          )
        );
        return false;
      }
    }
    if (roomId.length < 1) {
      this.disconnectWithLog(
        this.translateService.instant(
          'Unable to join the room. The room name entered is invalid. Please check and try again.'
        )
      );
      return false;
    }

    const ownUserId = this.userService.ownUserId;

    // Two tabs in the same browser share the same andorUUID. Without this
    // check both would join as the same userId and fight over per-user state
    // (peerId, userData flushes, audio reconcile). The lock is handed to
    // `_enterRoom`, which folds it into the RoomSession — held for the whole
    // session and released with it in disconnect().
    let lock = await acquireRoomPresenceLock(ownUserId, roomId);
    if (!lock) {
      this._connectionError.set({
        allowRetry: false,
        reason: this.translateService.instant(
          'You are already connected to this room in another browser tab. Please use that tab or close it before joining again here.'
        ),
      });
      this._connectionTransition.set('none');
      return false;
    }

    try {
      // Join an existing room, or — with `opts.createIfMissing` — open it on
      // demand as a hidden instant room (old invite links, typed room names). A
      // 404 ROOM_NOT_FOUND (only without createIfMissing) or 403 USER_BANNED is
      // surfaced by `_handleConnectError`.
      const tokenResponse = await this._fetchJoinToken(roomId, ownUserId, opts);
      // The server may resolve the requested id to a DIFFERENT room: an old
      // invite link / typed name whose slug matches a listed room's NAME joins
      // that room, and the token is minted for its real id. Adopt it — the
      // presence lock, cipher and `room-active` pings must all reference the
      // room actually joined. (Callers usually pre-resolve via
      // `AppService.resolveInviteRoom`, so this net is rarely taken.)
      const joinedRoomId = tokenResponse.roomId || roomId;
      if (joinedRoomId !== roomId) {
        const password = this._roomPasswords.get(roomId);
        if (password) {
          this._roomPasswords.set(joinedRoomId, password);
          this._roomPasswords.remove(roomId);
        }
        lock.release();
        lock = await acquireRoomPresenceLock(ownUserId, joinedRoomId);
        if (!lock) {
          this._connectionError.set({
            allowRetry: false,
            reason: this.translateService.instant(
              'You are already connected to this room in another browser tab. Please use that tab or close it before joining again here.'
            ),
          });
          this._connectionTransition.set('none');
          return false;
        }
      }
      return await this._enterRoom(joinedRoomId, tokenResponse, {
        // Keyed from RoomPasswordStore and the room's media key epoch. A
        // password-checked room refuses a wrong password before we get here
        // (RoomPasswordError); a room created before password checks still
        // admits it, and the mismatch only shows as undecryptable media.
        ...(await this._cipherFor(joinedRoomId, tokenResponse)),
        enforceWebrtcTransport,
        joinWithCam,
        lock,
      });
    } catch (e) {
      // Release whichever lock is currently held — the original, or the one
      // swapped in when the server redirected the join to a name-matched
      // room. If `_enterRoom` already folded it into the session, this is the
      // same lock object (release is idempotent) and the failure handlers
      // below drop the session itself.
      lock?.release();
      // Moderated access: not an error — show the waiting state, not a failure.
      if (e instanceof ApprovalRequiredError) {
        return this._enterApprovalPending(e.roomId, e.requestId);
      }
      if (e instanceof RoomPasswordError) {
        return this._enterPasswordRejected(roomId, e.reason);
      }
      return this._handleConnectError(e, enforceWebrtcTransport);
    }
  }

  /** Drop the held room claim, and with it the pin on the room's password. */
  private _dropSession(): void {
    this._session.set(null);
    this._roomPasswords.unpin();
  }

  /**
   * The server refused the room's password (none sent, or a wrong one). Like a
   * pending knock this is no connection failure: record the rejection under
   * the requested `roomId` — the id the pre-join screens classify — so they ask
   * for the password, and leave `connectionError` null.
   */
  private _enterPasswordRejected(roomId: string, reason: RoomPasswordRejection): false {
    // The caller already released the presence lock; just drop the claim.
    this._dropSession();
    this._connectionTransition.set('none');
    this._connectionError.set(null);
    this._roomPasswords.reject(roomId, reason);
    return false;
  }

  /**
   * Enter the "waiting for a moderator" state after a gated join (HTTP 202).
   * Records the pending request so the pre-join overlay can render, and so the
   * `join-approved` WS message can match it and re-join. Leaves `connectionError`
   * null — this is a normal wait, not a failure.
   */
  private _enterApprovalPending(roomId: string, requestId: string): false {
    // The caller already released the presence lock; just drop the claim.
    // Waiting for approval is deliberately session-LESS: the pending knock
    // lives beside a disconnected connection state.
    this._dropSession();
    this._connectionTransition.set('none');
    this._connectionError.set(null);
    this._pendingApproval.set({ roomId, requestId });
    return false;
  }

  /** Abandon a pending knock (user pressed Cancel on the waiting overlay). */
  cancelPendingApproval(): void {
    const pending = this._pendingApproval();
    this._pendingApproval.set(null);
    this._connectionTransition.set('none');
    // Best-effort: tell the server to withdraw the knock so the moderators'
    // "admit?" prompt clears immediately instead of lingering until our WS
    // disconnects or the request TTL expires. Fire-and-forget — the local UI
    // is already torn down regardless of the outcome.
    if (pending?.requestId) {
      firstValueFrom(
        this.roomsApi.cancelJoinRequest({
          requestId: pending.requestId,
        })
      ).catch(() => {});
    }
  }

  /**
   * Create a room server-side WITHOUT joining it: hit the create endpoint and
   * persist any password under the new roomId, but stop short of entering the
   * room: no
   * presence lock, no connection, no status change. The server treats this as a
   * pure room-open: the creator is NOT registered as a member (presence is
   * established separately over the notifications WebSocket), so the room starts
   * at `memberCount: 0`.
   *
   * Returns the new room's descriptor (roomId + metadata) or null on failure.
   * The freshly minted token is deliberately dropped: it lives only ~5 minutes
   * and must never be reused. When the user later joins this room, the normal
   * join flow ({@link connect} → `POST /v1/rooms/join`) mints a fresh token.
   * Callers should refresh the room list to surface the new room.
   */
  async createRoomOnly(
    roomName: string,
    opts: { isPrivate: boolean; password: string | null; schedule?: RoomSchedule }
  ): Promise<Omit<TCreateRoomResponse, 'kid' | 'token'> | null> {
    const ownUserId = this.userService.ownUserId;
    try {
      const tokenResponse = await this._fetchCreateToken(roomName, ownUserId, {
        isPrivate: opts.isPrivate,
        password: opts.password,
        schedule: opts.schedule,
      });
      if (opts.password) {
        this._roomPasswords.set(tokenResponse.roomId, opts.password);
      }
      return {
        roomId: tokenResponse.roomId,
        name: tokenResponse.name,
        isPrivate: tokenResponse.isPrivate,
        hasPassword: tokenResponse.hasPassword,
        schedule: tokenResponse.schedule,
      };
    } catch (e) {
      console.error('Failed to create room', e);
      return null;
    }
  }

  /**
   * The cipher for joining `roomId`: never the raw password, but the room's
   * media key for its current epoch (`deriveRoomMediaKey`), so whoever holds
   * one epoch's key (a recorder) learns neither the password nor any other
   * epoch's key. The epoch is the join response's, or a rotation that arrived
   * while joining. No cipher for a room without a password: a stored password
   * the room does not have is dropped, so invite links and room files never
   * claim encryption the room does not use. Also returns the password the
   * cipher is derived from, which the session pins.
   */
  private async _cipherFor(
    roomId: string,
    tokenResponse: TJoinRoomResponse
  ): Promise<{ cipher?: SetupCipherSettings; mediaKeyEpoch: number | null; password?: string }> {
    const password = this._roomPasswords.get(roomId);
    if (tokenResponse.hasPassword === false) {
      if (password) this._roomPasswords.remove(roomId);
      return { mediaKeyEpoch: null };
    }
    if (!password) return { mediaKeyEpoch: null };
    const pending = this._pendingMediaKeyEpoch;
    const mediaKeyEpoch = Math.max(
      tokenResponse.mediaKeyEpoch ?? 0,
      pending?.roomId === roomId ? pending.epoch : 0
    );
    return {
      cipher: {
        type: 'OdinCrypto',
        password: await deriveRoomMediaKey(password, roomId, mediaKeyEpoch),
      },
      mediaKeyEpoch,
      password,
    };
  }

  /**
   * A `room-media-key` rotation: re-key the held room's cipher for `epoch`
   * (the server rotates when a recording starts and when it ends). A rotation
   * for a room still being joined is kept and applied right after the join.
   * Only ever moves forward, so repeats and late replays are no-ops.
   */
  applyMediaKeyEpoch(roomId: string, epoch: number): void {
    const session = this._session();
    if (session?.roomId !== roomId) {
      if (this.isConnecting() && epoch > (this._pendingMediaKeyEpoch?.epoch ?? -1)) {
        this._pendingMediaKeyEpoch = { roomId, epoch };
      }
      return;
    }
    const pending = this._pendingMediaKeyEpoch;
    if (pending?.roomId !== roomId || epoch > pending.epoch) {
      this._pendingMediaKeyEpoch = { roomId, epoch };
    }
    void this._rotateMediaKey(session);
  }

  /** Re-key `session`'s cipher for its latest known epoch, if newer. */
  private async _rotateMediaKey(session: RoomSession): Promise<void> {
    const target = this._pendingMediaKeyEpoch;
    const room = this._odinRoom.getValue();
    const cipher = room?.cipher;
    const password = this._roomPasswords.get(session.roomId);
    const current = session.mediaKeyEpoch();
    // Not joined yet (`_enterRoom` re-runs this after the join), unencrypted,
    // or not newer.
    if (!room || !cipher || !password || current === null) return;
    if (target?.roomId !== session.roomId || target.epoch <= current) return;
    let key: string;
    try {
      key = await deriveRoomMediaKey(password, session.roomId, target.epoch);
    } catch (e) {
      console.error('Could not derive the room media key', e);
      return;
    }
    // A newer rotation, a reconnect or another session may have won meanwhile.
    const latest = session.mediaKeyEpoch();
    if (this._session() !== session || this._odinRoom.getValue() !== room) return;
    if (latest === null || target.epoch <= latest) return;
    // The plugin holds one key at a time: until every peer switched too
    // (well under a second), audio and messages between us are lost. Accepted.
    cipher.configure({ password: key });
    session.mediaKeyEpoch.set(target.epoch);
  }

  /**
   * The next media key epoch of the held encrypted room and its key, for a
   * start-recording request (starting a recording rotates the room's key, so
   * the recorder never holds the key of earlier audio): the one after
   * `currentEpoch` (the server's, when it reported ours stale) or after the
   * cipher's. `null` when no encrypted room is held.
   */
  async nextMediaKey(
    currentEpoch?: number
  ): Promise<{ mediaKeyEpoch: number; mediaKey: string } | null> {
    const session = this._session();
    const current = session?.mediaKeyEpoch() ?? null;
    const password = session && this._roomPasswords.get(session.roomId);
    if (!session || current === null || !password) return null;
    const mediaKeyEpoch = (currentEpoch ?? current) + 1;
    return {
      mediaKeyEpoch,
      mediaKey: await deriveRoomMediaKey(password, session.roomId, mediaKeyEpoch),
    };
  }

  /**
   * Shared "enter the ODIN room with a token" path for both join and create.
   * Takes ownership of the caller-acquired presence lock, folding it into the
   * {@link RoomSession} that marks this room as held. Writes the authoritative
   * roomId + display name into settings, then joins. Throws on failure — the
   * caller releases the lock and maps the error via `_handleConnectError`,
   * which drops the session.
   */
  private async _enterRoom(
    roomId: string,
    tokenResponse: TJoinRoomResponse,
    opts: {
      cipher?: SetupCipherSettings;
      /** The epoch `cipher` is keyed for; `null` without a cipher. */
      mediaKeyEpoch?: number | null;
      /** The room password `cipher` is derived from. */
      password?: string;
      enforceWebrtcTransport?: boolean;
      joinWithCam: boolean;
      lock: RoomPresenceLock | null;
    }
  ): Promise<boolean> {
    const { cipher, enforceWebrtcTransport, joinWithCam } = opts;
    // The session exists from here — before the SDK join resolves — so the
    // keepalive and rejoin sends already cover the join-in-flight window.
    const session = new RoomSession(roomId, opts.lock);
    session.mediaKeyEpoch.set(cipher ? (opts.mediaKeyEpoch ?? 0) : null);
    this._session.set(session);
    // Other tabs share the password store: keep this call on its key.
    this._roomPasswords.pin(roomId, cipher ? (opts.password ?? null) : null);

    // Server response is authoritative for identity + display name.
    this._userSettingsStore.update({
      roomId,
      ...(tokenResponse.name ? { roomName: tokenResponse.name } : {}),
    });

    // Fail-safe: never enter a room transmitting without the privilege or a
    // live talk grant. The revocation subscription only fires on true->false
    // transitions, which misses e.g. a one-shot grant consumed in a previous
    // room while the user stayed unmuted through the room switch.
    if (
      !this._permissionStore.value.useMicrophone &&
      !this._userSettingsStore.value.talkPermissionGranted &&
      this._userSettingsStore.value.mutedState === AudioMuteStates.none
    ) {
      this._userSettingsStore.update({ mutedState: AudioMuteStates.mic });
    }

    const room = this._roomFactory.create();
    this._odinRoom.next(room);
    if (
      this._userSettingsStore.value.mutedState === AudioMuteStates.speakers ||
      this._userSettingsStore.value.mutedState === AudioMuteStates.speakersKeepMic
    ) {
      room.setVolume(0).catch(console.error);
    }
    await this.roomEventHandlers(room);
    this.startAudioOutput().catch(console.error);
    // The saved region, never an unsaved pick in App Settings.
    let gateway = `https://${this._appConfigStore.saved().gateway}`;

    // Override the default gateway with the user address configured in the
    // developer options. A bare host gets the same https:// prefix as the
    // default path; an explicit scheme (e.g. http:// for a local server) is
    // kept as-is.
    const userGateway = this._userSettingsStore.value.serverAddress.trim();
    if (this._appConfigStore.value.developerMode && userGateway.length > 0) {
      gateway = /^https?:\/\//.test(userGateway) ? userGateway : `https://${userGateway}`;
    }

    this.userService.updateUserData({ joinedAt: Date.now() });
    const joinParams = {
      gateway,
      transport: this.getTransport(enforceWebrtcTransport),
      userData: this.encodeUserData(),
      cipher,
    };
    // Never log key material.
    console.log('Joining room with params: ', {
      ...joinParams,
      cipher: cipher && { type: cipher.type, password: '[redacted]' },
    });
    await room.join(tokenResponse.token, joinParams);
    // A rotation that arrived while joining could not reach the cipher yet.
    void this._rotateMediaKey(session);
    // Mic attach is handled by the reconcile subscription in the constructor:
    // it holds off while the session's media is still 'joining' and attaches
    // (creating the AudioInput lazily if needed) once onJoined flips it to
    // 'joined' — after the peer replay told us whether a transcription bot is
    // in the room and recording consent is required.
    if (joinWithCam) {
      this.startCam().catch(console.error);
    }
    // Register presence on the server's active-room registry NOW rather than
    // waiting up to 30s for the first keepalive tick. This both makes us visible
    // for room-scoped knock notifications immediately and triggers the server's
    // catch-up replay of any knocks already pending in this room (so a moderator
    // entering a room where someone is already waiting sees the request at once).
    this._realtime.send({ type: 'room-active', roomId });
    return true;
  }

  /**
   * Resets connection state and maps a connect/create failure to a
   * user-facing connectionError. Recognises the backend's structured room
   * errors (ROOM_NOT_FOUND, USER_BANNED, APP_TRIAL_EXPIRED) and otherwise
   * falls back to the generic transport message.
   */
  private _handleConnectError(e: unknown, enforceWebrtcTransport?: boolean): false {
    console.error(e);
    // The caller already released the presence lock; just drop the claim and
    // end the connect transition — the branches below only pick the message.
    this._dropSession();
    this._connectionTransition.set('none');

    if (e instanceof HttpErrorResponse) {
      const body = (e.error ?? {}) as RoomErrorBody;
      if (e.status === 404 && body.code === 'ROOM_NOT_FOUND') {
        this._connectionError.set({
          allowRetry: false,
          reason: this.translateService.instant('This room no longer exists or was never opened.'),
        });
        return false;
      }
      if (e.status === 403 && body.code === 'USER_BANNED') {
        const reason = body.bannedUntil
          ? this.translateService.instant('You have been banned from this room until [time].', {
              time: formatDate(body.bannedUntil, 'timestamp', this._language.locale()),
            })
          : this.translateService.instant('You have been banned from this room.');
        this._connectionError.set({ allowRetry: false, reason });
        return false;
      }
      if (e.status === 403 && body.code === 'APP_TRIAL_EXPIRED') {
        this._connectionError.set({
          allowRetry: false,
          reason: this.translateService.instant(
            'Trial expired. Joining rooms is no longer possible.'
          ),
        });
        return false;
      }
    }

    // Generic transport-based fallback.
    // The WebRTC fallback this offers does not exist in the native backend.
    const allowRetry = !isTauri && this.getTransport(enforceWebrtcTransport) === 'H3';
    const reason = allowRetry
      ? this.translateService.instant(
          'Unable to join the room using WebTransport. Your browser seems to support WebTransport over HTTP/3, but something appears to be blocking the connection. Would you like to try joining the room using our fallback protocol instead?'
        )
      : this.translateService.instant(
          'Unable to join the room. Please verify your internet connection and try again. If the issue persists, consider restarting your connection or reaching out for assistance.'
        );
    this._connectionError.set({ allowRetry, reason });
    return false;
  }

  getTransport(enforceWebrtcTransport?: boolean): Transport {
    // The native backend only speaks H3; the WebView's WebTransport support
    // is irrelevant there.
    if (isTauri) return 'H3';
    if (isH3Supported() && !this._userSettingsStore.value.useWebRtc) {
      if (config.enforceWebrtcTransport || enforceWebrtcTransport) {
        console.warn(
          'This browser would support H3 connections; enforcing WebRTC transport for now'
        );
      } else {
        return 'H3';
      }
    }
    return 'WebRTC';
  }

  disconnectWithLog(reason: string) {
    console.error(reason);
    this.disconnect({ reason }).then();
  }

  isConnecting(): boolean {
    return this._derivedStatus() === OdinConnectionStatus.connecting;
  }

  isDisconnecting(): boolean {
    const connectionStatus = this._derivedStatus();
    return (
      connectionStatus === OdinConnectionStatus.disconnecting ||
      connectionStatus === OdinConnectionStatus.disconnected
    );
  }

  private encodeUserData(): Uint8Array {
    const userData = this.usersStore.get(this.userService.ownUserId)?.data;
    let encodedData = new Uint8Array();
    try {
      if (userData) {
        encodedData = new Uint8Array(valueToUint8Array(userData));
      }
    } catch (e) {
      console.error('Could not encode data');
    }
    return encodedData;
  }

  /**
   * Mint a token for an EXISTING room. Server reads the user id from the
   * session JWT, not the body. A non-2xx (404 ROOM_NOT_FOUND / 403
   * USER_BANNED) rejects and is mapped by `_handleConnectError`.
   */
  private async _fetchJoinToken(
    roomId: string,
    ownUserId: string,
    opts?: { createIfMissing?: boolean; roomName?: string }
  ): Promise<TJoinRoomResponse> {
    const accessKey = this._userSettingsStore.value.accessKey;
    if (accessKey && accessKey.length > 1 && this._appConfigStore.value.developerMode) {
      const generator = new TokenGenerator(accessKey);
      const params: TokenOptions = {};
      if (this._userSettingsStore.value.isSFUConnection) {
        params.audience = 'sfu';
      }
      const token = await generator.createToken(roomId, ownUserId, params);
      // No server: the stored password alone decides encryption.
      return {
        kid: 'dev',
        token,
        roomId,
        hasPassword: !!this._roomPasswords.get(roomId),
        mediaKeyEpoch: 0,
      };
    }
    const password = this._roomPasswords.get(roomId);
    // When opening on demand, tell the server whether to make the new room
    // end-to-end encrypted from whether we hold a cipher password for it (typed,
    // or recovered from an invite link's `#p=` fragment). No password ⇒ open.
    // Ignored server-side for a room that already exists (the first opener's
    // E2EE mode wins), so this only decides a room WE create here.
    const createBody = opts?.createIfMissing
      ? { createIfMissing: true, roomName: opts.roomName, hasPassword: !!password }
      : {};
    // `observe: 'response'` so we can distinguish the moderated-access 202 from a
    // normal 200: a 202 is a SUCCESS (no token), so without inspecting the status
    // the schema parse below would throw and mis-route to a transport error.
    const join = (passwordProof?: RoomPasswordProof) =>
      firstValueFrom(
        this.roomsApi.joinRoom(
          {
            roomId,
            userName: this._userSettingsStore.value.userName,
            ...createBody,
            ...(passwordProof ? { passwordProof } : {}),
          },
          { observe: 'response' }
        )
      );
    // Holding a password, always prove it: a password-checked room requires the
    // proof, and on open-on-demand it makes the new room checked. The proof is
    // bound to the canonical room id.
    let provedFor = normalizeRoomId(roomId) ?? roomId;
    let response: Awaited<ReturnType<typeof join>>;
    for (let attempt = 0; ; attempt++) {
      try {
        // Start the media key root (as slow as the proof) alongside: the
        // cipher needs it right after this request.
        if (password) roomMediaRoot(password, provedFor).catch(() => {});
        response = await join(
          password ? await deriveRoomPasswordProof(password, provedFor) : undefined
        );
        break;
      } catch (e) {
        const rejection = passwordRejection(e);
        if (!rejection) throw e;
        // A name-slug match redirected the join to another room: the proof is
        // bound to the real id, so derive it for that id and retry once.
        const realId = rejection.roomId;
        if (password && realId && realId !== provedFor && attempt === 0) {
          provedFor = realId;
          continue;
        }
        throw new RoomPasswordError(password ? 'invalid' : 'required');
      }
    }
    const body = response.body;
    if (response.status === 202 || (body as RoomErrorBody | null)?.code === 'APPROVAL_REQUIRED') {
      throw new ApprovalRequiredError(roomId, (body as RoomErrorBody | null)?.requestId ?? '');
    }
    return joinRoomResponseSchema.parse(body);
  }

  /**
   * Create a new room and mint its token. The roomId is generated here, so a
   * `password` can be proven for it in the same request (the server stores the
   * proof's hash and checks joins against it); the response's roomId stays
   * authoritative. In developer mode (local access key) there is no server, so
   * the token is minted client-side.
   */
  private async _fetchCreateToken(
    roomName: string,
    ownUserId: string,
    opts: {
      isPrivate: boolean;
      password: string | null;
      isHidden?: boolean;
      schedule?: RoomSchedule;
    }
  ): Promise<TCreateRoomResponse> {
    const roomId = crypto.randomUUID();
    const hasPassword = !!opts.password;
    const accessKey = this._userSettingsStore.value.accessKey;
    if (accessKey && accessKey.length > 1 && this._appConfigStore.value.developerMode) {
      const generator = new TokenGenerator(accessKey);
      const params: TokenOptions = {};
      if (this._userSettingsStore.value.isSFUConnection) {
        params.audience = 'sfu';
      }
      const token = await generator.createToken(roomId, ownUserId, params);
      return {
        kid: 'dev',
        token,
        roomId,
        name: roomName,
        isPrivate: opts.isPrivate,
        hasPassword,
        isHidden: opts.isHidden ?? false,
        schedule: opts.schedule,
      };
    }
    const request = this.roomsApi.createRoom({
      roomName,
      roomId,
      isPrivate: opts.isPrivate,
      hasPassword,
      ...(opts.password
        ? { passwordProof: await deriveRoomPasswordProof(opts.password, roomId) }
        : {}),
      ...(opts.isHidden ? { isHidden: true } : {}),
      ...(opts.schedule ? { schedule: opts.schedule } : {}),
    });
    const result = await firstValueFrom(request);
    return createRoomResponseSchema.parse(result);
  }

  async roomEventHandlers(room: Room) {
    room.onJoined = (payload) => this._onRoomJoined(payload);
    room.onPeerJoined = (payload) => this._onPeerJoined(payload);
    room.onPeerLeft = (payload) => this._onPeerLeft(payload);
    room.onAudioOutputStarted = (payload) => this._onAudioOutputStarted(payload);
    room.onAudioOutputStopped = (payload) => this._onAudioOutputStopped(payload);
    room.onVideoOutputStarted = (payload) => this._onVideoOutputStarted(payload);
    room.onVideoOutputStopped = (payload) => this._onVideoOutputStopped(payload);
    room.onAudioActivity = (payload) => this._onAudioActivity(payload);
    room.onMessageReceived = (payload) => this._onMessage(payload);
    room.onUserDataChanged = (payload) => this._onUserDataChanged(payload);
    room.onStatusChanged = (payload) => this._onStatusChanged(payload);
  }

  async disconnect(
    error?: OdinConnectionError,
    suppressSound?: boolean,
    resetView: boolean = true
  ) {
    if (this.isDisconnecting()) {
      return;
    }
    // Intent-first: `disconnecting` outranks the still-live session in the
    // derived status, keeping the peer-event gates closed through the
    // leave() teardown storm below.
    this._connectionTransition.set('disconnecting');
    if (!suppressSound)
      this.playSound(soundDisconnected, this._userSettingsStore.value.playAppSounds).catch((e) =>
        console.error(e)
      );
    this._connectionError.set(error ?? null);
    const room = this._odinRoom.getValue();
    room?.leave();
    // End the room claim after leave, so the session (and its cross-tab
    // presence lock) spans the full connected lifetime. Kept out of the media
    // teardown below because that also runs on reconnect, where the session
    // must survive.
    this._session()?.release();
    this._dropSession();
    // Mic release is handled by the reconcile subscription when _odinRoom flips
    // to null below: with no room and (typically) no mic-test surface active, it
    // sets the AudioInput to `setVolume('muted')`, which stops the underlying
    // MediaStream in the SDK. No explicit stopAudioInput needed.
    await this._teardownMediaConnection();
    if (resetView) {
      this.appStore.update({
        isConnectedView: false,
        loginAreaLogoSize: this._appService.getResponsiveLogoSize(),
      });
    }
    this.userService.updateUserData({ breakoutRoomId: '' }); // leave breakout room on intentional disconnect
    this._connectionTransition.set('none');
    this._streamStats.registerWebStatsSource(null);
    this._odinRoom.next(null);
  }

  /**
   * Wipe the service-owned projections of the CURRENT media connection —
   * speaker/audio-activity tracking, the remote user entities, and local
   * capture streams. The media-cycle resources themselves (per-cycle
   * subscriptions, transcription-bot marker) are owned by the
   * {@link RoomSession} and end via its media-cycle methods.
   *
   * This runs on EVERY media `reconnecting` transition, not only when
   * leaving. Anything that must survive a reconnect belongs elsewhere:
   * session-scoped state (the {@link RoomSession} claim + presence lock, the
   * connected view) is torn down only in `disconnect()`, and service-lifetime
   * work (the presence keepalive, the `room-rejoin` send) lives in the
   * constructor guarded by the session.
   */
  private async _teardownMediaConnection(): Promise<void> {
    this._activeSpeakerTracker.resetAll();
    this._audioActivity.resetAll();
    // Wipe the room — drop every remote entity but keep the stable
    // own-user entity so its `peerId` can be reset in place rather than
    // re-keyed on the next join.
    const ownUserId = this.userService.ownUserId;
    const ownUser = this.userService.ownUser();
    this.usersStore.store.update(
      setEntitiesMap(ownUser ? { [ownUserId]: { ...ownUser, peerId: null } } : {})
    );
    this._localStream.stopScreenCastStream();
    this._localStream.stopCamStream('reset');
  }

  private async _onRoomJoined(payload: JoinedPayload) {
    // Room creation is now an explicit, server-`manageRooms`-gated action
    // (`createRoom`), and rooms persist even when empty — so joining a
    // legitimately-empty room is valid. The old "peers.size <= 1 means you
    // created it" heuristic no longer applies and has been removed.

    // A stale onJoined (e.g. an SDK event racing a disconnect) must never
    // resurrect a connected state after the session ended.
    const session = this._session();
    if (!session) return;
    const user = this.userService.ownUser();
    if (!user) return;
    // Own entity is stable across reconnects; only swap the current peerId
    // in place — no re-keying. Must happen BEFORE the media flips to joined
    // (see the own-peerId window comment in user.repository.ts).
    this.userService.setPeerId(payload.peer.id);
    // Fires on the initial join AND after every SDK-internal reconnect —
    // markJoined() ends the previous media cycle and starts a fresh one.
    // The joined session derives `connected`; the connect transition (if this
    // was the initial join) is over.
    const rejoined = session.hasJoined;
    session.markJoined();
    // The peer replay preceding this onJoined is authoritative for bot
    // presence: no bot after a reconnect means the recording ended while we
    // were away — the bot leave we never observed. (A still-present bot keeps
    // the session's consent; this is NOT a per-join reset.)
    if (!session.transcriptionBot()) {
      session.resetRecordingConsent();
    }
    if (rejoined) {
      this._mediaRejoined.next({
        roomId: session.roomId,
        botPresent: session.transcriptionBot() !== null,
      });
    }
    this._connectionTransition.update((t) => (t === 'connecting' ? 'none' : t));
    this.appStore.update({
      isConnectedView: true,
      loginAreaLogoSize: 'invisible',
    });
    this.playSound(soundConnected, this._userSettingsStore.value.playAppSounds).catch(
      console.error
    );
    // Per-media-cycle subscription: mediaStop$ is read AFTER markJoined(), so
    // it scopes to the fresh cycle and ends on the next reconnect/teardown.
    this.userService.ownUserData$.pipe(takeUntil(session.mediaStop$)).subscribe((userData) => {
      if (userData) {
        this.updateUserData(userData);
      }
    });
    // The `room-active` keepalive is NOT armed here: it lives in the
    // constructor for the service lifetime, so it survives media trouble.
  }

  async startAudioOutput() {
    const device = await this._deviceService.refreshAudioOutputDevices();
    if (!device) {
      //@TODO show warning that there is no playback
      return;
    }
    console.log('startAudioOutput', device);
    await this._userActivation.waitForActivation();
    console.log('waited for startAudioOutput', device);
    setOutputDevice({ device }).catch(error);
  }

  // Creates the AudioInput object if it doesn't exist yet. Idempotent and safe
  // to call concurrently — multiple callers awaiting the same in-flight start
  // will receive the same result. Attaching to a room and muting/unmuting is
  // handled by the reconcile subscription, not here.
  ensureAudioInputExists(): Promise<boolean> {
    if (this.audioInput) return Promise.resolve(true);
    if (this._audioInputCreationInFlight) return this._audioInputCreationInFlight;
    this._audioInputCreationInFlight = this._doEnsureAudioInputExists().finally(() => {
      this._audioInputCreationInFlight = null;
    });
    return this._audioInputCreationInFlight;
  }

  private async _doEnsureAudioInputExists(): Promise<boolean> {
    this._isAudioStarting.set(true);
    try {
      await this._permissions.waitForMediaApproval();
      await this._userActivation.waitForActivation();
      const permission = await this._permissions.requestMic();
      if (permission !== 'granted') return false;
      const audioInput = await DeviceManager.createAudioInput(
        {
          device: this._deviceService.activeAudioInputDevice,
          onStatusChanged: (status) => this._onCaptureStatusChanged(status),
        },
        {
          voiceActivity: this.voiceActivityFromBool(
            this._userSettingsStore.value.voiceActivityDetection
          ),
          volumeGate: this._userSettingsStore.value.volumeGate,
          volume: this._userSettingsStore.value.inputVolume,
          echoCanceller: this._mediaConfigStore.value.echoCancellation,
          noiseSuppression: this._mediaConfigStore.value.noiseSuppression,
          gainController: this._mediaConfigStore.value.autoGainControl,
        }
      );
      this._audioInput.next(audioInput);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      this._isAudioStarting.set(false);
    }
  }

  /**
   * Detaches, closes and forgets the AudioInput, so the next capture request
   * creates a fresh one through ensureAudioInputExists() — and with it
   * requestMic(). Used when browser access is revoked: the plugin keeps a
   * dead capture that no `setVolume` re-opens.
   */
  private _dropAudioInput(): void {
    const input = this._audioInput.value;
    if (!input) return;
    this._odinRoom.value?.removeAudioInput(input);
    input.close();
    this._audioInput.next(null);
  }

  /**
   * Capture status feed from the plugin (see OnStatusChanged in the plugin
   * API). `'stopped'`: the selected device vanished and the plugin closed
   * the capture. `'defaulted'`: the capture opened on the default device
   * because the selected one was unavailable (e.g. unplugged while muted).
   * Both are answered with a device-list refresh: resolution flips the
   * active device to Default, which the activeAudioInputDevice$
   * subscription turns into a re-opened capture and the pickers render
   * truthfully again. The STORED selection is deliberately left alone
   * (healSelection's contract) so a returning device is re-selected.
   * Typed as string — the contract allows new status values in minor
   * releases.
   */
  private _onCaptureStatusChanged(status: string): void {
    log(3, 'AudioInput status changed', { status });
    if (status === 'stopped' || status === 'defaulted') {
      this._deviceService.refreshAudioInputDevices().catch(console.error);
    }
  }

  // The ODIN SDK guarantees this firing order:
  //   1) `onPeerJoined` for every remote peer already in the room
  //   2) `onJoined` for the own peer (flips `_status` to `connected`)
  //   3) `onPeerJoined` for every remote peer joining in the future
  // Gating chat notices and sounds on `_status === connected` therefore
  // suppresses the initial snapshot (phase 1) and only emits for genuine new
  // joins (phase 3). The same applies on reconnect: `_status` is
  // `reconnecting` when the SDK re-announces existing peers, so those
  // re-snapshot events are suppressed too and only post-reconnect new joins
  // surface in chat. The activity log is the exception: phase-1 peers were
  // in the room before us, so their presence is backfilled (idempotently)
  // from their self-reported `joinedAt` instead of being dropped.
  private async _onPeerJoined(payload: PeerJoinedPayload) {
    const peer = payload.peer;
    if (!peer.isRemote) return;
    // Stable identity comes from the ODIN token claim (`peer.userId`), not
    // from userData. If the token wasn't minted by our backend it'll be
    // empty — skip rather than create an unaddressable entity.
    if (!peer.userId) {
      console.error('peer.userId missing in onPeerJoined; skipping', peer);
      return;
    }
    if (peer.userId === TRANSCRIPTION_BOT_USER_ID) {
      const session = this._session();
      if (!session) return;
      // Our own start-recording request spawned this bot: the starter is never
      // prompted. Decided BEFORE the marker flips so the prompt never flashes.
      if (session.consumeOwnRecording()) {
        session.recordingConsent.set('given');
      }
      session.transcriptionBot.set({ peerId: peer.id });
      // `connected` only for a live join, not for the phase-1 replay of a join
      // or reconnect (the recording was already running).
      const live = this.status() === OdinConnectionStatus.connected;
      this._transcriptionBotEvents.next({
        kind: 'joined',
        roomId: session.roomId,
        peerId: peer.id,
        live,
      });
      // A recording STARTING while we're in the room gets a sound (starter
      // included — it confirms the bot arrived). Not on the phase-1 replay
      // (initial join: the consent dialog speaks for itself; reconnect: the
      // recording was already running). An app sound, like
      // connected/disconnected: gated on playAppSounds, not on the
      // joined/left-sounds toggle.
      if (live) {
        this.playSound(soundRecordingStarted, this._userSettingsStore.value.playAppSounds).catch(
          console.error
        );
      }
      return;
    }
    let user: User | undefined;
    try {
      const data = uint8ArrayToValue(peer.data);
      user = this.userService.createUser(peer.userId, peer.id, data);
    } catch (e) {
      user = this.userService.createUser(peer.userId, peer.id, {});
      console.error(e);
      console.error(`Could not decode the UserData from peer ${peer.userId}`);
    }
    this._audioActivity.seedForPeer(peer.userId, peer.isActive);

    if (this.status() === OdinConnectionStatus.connected) {
      this.playSound(soundPeerJoined).catch(console.error);
      this._activityLog.recordJoin(user);
    } else {
      // Phase-1 snapshot (initial connect or reconnect replay): the peer was
      // already in the room. Backfill their join using their self-reported
      // joinedAt; recordPresence dedups replays via a deterministic id.
      this._activityLog.recordPresence(user);
    }
  }

  // When the own user disconnects, `onPeerLeft` fires once for every remote
  // peer that was still connected. The same happens when the room enters
  // `reconnecting` — the SDK drops all peers and re-announces them after the
  // connection comes back. `disconnect()` flips `_status` to `disconnecting`
  // before calling `room.leave()`, and since the SDK's status-before-reset
  // fix (first release after 1.7.1) the room status — and with it `_status`
  // via `_onStatusChanged` — flips to `reconnecting` before the reconnect
  // teardown events are dispatched. Gating on `connected` below therefore
  // suppresses both teardown sequences and only emits for peers actually
  // leaving during a live session. (On 1.7.1 and earlier the teardown was
  // dispatched while the status was still `joined`, so reconnect teardown
  // is indistinguishable from live leaves until the SDK update ships.)
  private async _onPeerLeft(payload: PeerLeftPayload) {
    if (!payload.peer.isRemote) return;
    if (!payload.peer.userId) return;
    if (payload.peer.userId === TRANSCRIPTION_BOT_USER_ID) {
      const session = this._session();
      // Only the bot we track: a stale leave of a previous bot peer (Cortex
      // rejoined it under a new peer id) must not end the running recording.
      if (session?.transcriptionBot()?.peerId !== payload.peer.id) return;
      session.transcriptionBot.set(null);
      // A LIVE leave (the recording stopped) resets consent — the next
      // recording must ask again. The SDK also fires onPeerLeft for every peer
      // on the reconnecting/disconnect teardown, but the status has left
      // `connected` by then, and consent must survive a reconnect.
      if (this.status() === OdinConnectionStatus.connected) {
        session.resetRecordingConsent();
        this._transcriptionBotEvents.next({
          kind: 'left',
          roomId: session.roomId,
          peerId: payload.peer.id,
          live: true,
        });
        this.playSound(soundRecordingStopped, this._userSettingsStore.value.playAppSounds).catch(
          console.error
        );
      }
      return;
    }

    // Stale-leave guard: on a rapid remote reconnect the SDK can deliver
    // onPeerLeft for the old peer.id after onPeerJoined has already
    // replaced the stored peerId with the new one. Treat that case as a
    // no-op — playing the leave sound, recording the activity-log entry,
    // or nulling the (new) peerId would all be wrong since the user is
    // still in the room.
    const user = this.usersStore.get(payload.peer.userId);
    if (user && user.peerId !== payload.peer.id) return;

    if (this.status() === OdinConnectionStatus.connected) {
      this.playSound(soundPeerLeft);
    }
    if (this.status() === OdinConnectionStatus.connected && user) {
      this._activityLog.recordLeft(user);
    }
    this._activeSpeakerTracker.clear(payload.peer.userId);
    this._audioActivity.clear(payload.peer.userId);
    // Null out peerId rather than delete so message history /
    // participant-settings survive across reconnects within the same
    // session. Full pruning happens in _teardownMediaConnection().
    this.usersStore.setPeerId(payload.peer.userId, null);
  }

  private async _onAudioOutputStarted(payload: AudioInputPayload | AudioOutputPayload) {
    if (payload.peer.isRemote) {
      const user = this.usersStore.get(payload.peer.userId);
      if (!user) return;
      const updatedUser: User = {
        ...user,
      };
      if (payload.media.kind === 'audio-output') {
        if (user.data.breakoutRoomId !== this.userService.ownUser()?.data.breakoutRoomId) {
          this.room?.removeAudioOutput(payload.media);
        }
        updatedUser.audioOutput = payload.media;
        const volume = this._participantSettingsStore.get(user.id)?.volume;
        if (volume !== null && typeof volume !== 'undefined') {
          updatedUser.audioOutput.setVolume(volume);
        }
      }
      this.usersStore.update(updatedUser);
    }
  }

  // Stale-stop guard (counterpart to the stale-leave guard in _onPeerLeft):
  // on a rapid remote reconnect the SDK can deliver the old peer's
  // AudioOutputStopped after the new peer's AudioOutputStarted has already
  // attached the fresh media. Clearing unconditionally would wipe the new
  // audioOutput — and the user-avatar renders a missing audioOutput as
  // muted, so the user would stay "muted" in the users list until their
  // media restarts. Only clear when the stopped media is the one we hold.
  private async _onAudioOutputStopped(payload: AudioOutputPayload) {
    const user = this.usersStore.get(payload.peer.userId);
    if (!user || user.audioOutput !== payload.media) return;
    const updatedUser: User = {
      ...user,
      audioOutput: undefined,
    };
    this.usersStore.update(updatedUser);
  }

  // @TODO Only update the user that he has now a video but don't start it, this will be handled by the tile (new method)
  private async _onVideoOutputStarted(payload: VideoOutputPayload) {
    const user = this.usersStore.get(payload.peer.userId);
    const room = this._odinRoom.value;
    const video = payload.media;
    if (!user || !room) return;
    const updatedUser: User = {
      ...user,
    };
    if (payload.media.customType === 'cam') {
      updatedUser.camStream = video;
    }
    if (payload.media.customType === 'screen-cast') {
      updatedUser.screenCastStream = video;
    }
    this.usersStore.update(updatedUser);
  }

  // @TODO Add new method that just stops and starts
  private async _onVideoOutputStopped(payload: VideoOutputPayload) {
    const user = this.usersStore.get(payload.peer.userId);
    const video = payload.media;
    const room = this.room;
    if (!user || !room) return;
    room.stopVideoOutput(video);
    // Same stale-stop guard as _onAudioOutputStopped: a late stop event for the
    // old peer's media must not wipe the stream the reconnected peer just
    // started. Only clear the slot that actually holds the stopped media.
    const updatedUser: User = {
      ...user,
    };

    if (video.customType === 'cam' && user.camStream === video) {
      updatedUser.camStream = undefined;
    }
    if (video.customType === 'screen-cast' && user.screenCastStream === video) {
      updatedUser.screenCastStream = undefined;
    }

    this.usersStore.update(updatedUser);
  }

  private async _onAudioActivity(payload: AudioActivityPayload) {
    const userId =
      payload.media.kind === 'audio-input' ? this.userService.ownUserId : payload.media.peer.userId;
    if (!userId) return;
    this._activeSpeakerTracker.track(userId, payload.media.isActive);
    this._audioActivity.set(userId, payload.media.isActive);
  }

  private _onStatusChanged(payload: RoomStatusChangedPayload) {
    if (payload.newState.status === 'reconnecting') {
      // Flip the media sub-state (ending the media cycle) BEFORE wiping the
      // store projections — the SDK dispatches its synthetic peer-teardown
      // events after the status change, and the gates in the peer handlers
      // must already see the non-joined state. A stale event with no held
      // session derives no change (stays disconnected).
      this._session()?.markReconnecting();
      this._teardownMediaConnection().catch(error);
    }
    if (payload.newState.status === 'disconnected') {
      if (payload.oldState.status === 'reconnecting') {
        this.disconnect({
          reason: this.translateService.instant(
            'Your connection to the room has been interrupted. This could be caused by a network issue or because the server is no longer available.'
          ),
        }).catch(error);
      } else {
        console.log('Status Changed Disconnected: ');
        this.disconnect(undefined, true, false).catch(error); // suppress sound during connect
      }
    }
  }

  async startRemoteVideo(video: VideoOutput) {
    const room = this._odinRoom.value;
    if (!room) return;
    await room.startVideoOutput(video);
  }

  /**
   * How the stream stats find a video: natively by the server's media id,
   * in the browser by the rendered track.
   */
  videoStatsTarget(video: VideoInput | VideoOutput): StatsTarget {
    const send = video.kind === 'video-input';
    const direction = send ? 'send' : 'receive';
    let stream: MediaStream | undefined;
    try {
      stream = video.mediaStream ?? undefined;
    } catch {
      // A VideoInput throws until its capture has started.
    }
    if (isTauri) {
      const mediaId = send
        ? (
            this._plugin as { getLocalVideoMediaId?: (type: string) => number | undefined }
          )?.getLocalVideoMediaId?.(video.customType ?? 'cam')
        : video.mediaId;
      return { direction, mediaId, stream };
    }
    return { direction, trackId: stream?.getVideoTracks()[0]?.id, stream };
  }

  async stopRemoteVideo(video: VideoOutput) {
    const room = this._odinRoom.value;
    if (!room) return;
    room.stopVideoOutput(video);
  }

  private async _onMessage(payload: MessageReceivedPayload) {
    if (!payload.message || !payload.peer) return;
    const peer = payload.peer;
    // The transcription bot has no users-store entry and speaks the Cortex
    // Room Protocol, not chat. Its user id comes from the token, which our
    // backend mints for every andor user with their own UUID, so no other
    // peer can send as the bot.
    if (peer.userId === TRANSCRIPTION_BOT_USER_ID) {
      this._onTranscriptionBotMessage(peer.id, payload.message);
      return;
    }
    if (peer) {
      const user = this.usersStore.get(peer.userId);
      if (user) {
        try {
          const messageData = this.chatService.parseMessage(payload.message);
          this.chatService
            .buildMessage(user, messageData, this.userService.ownUser()!)
            .catch((e) => console.error(e));
        } catch (e) {
          console.warn("Couldn't decode message from: ", peer);
        }
      }
    }
  }

  /**
   * Decode a transcription-bot message as a Cortex Room Protocol frame and
   * hand it on. Anything else is dropped: oversized messages, non-JSON (a
   * message the cipher could not decrypt arrives empty) and other formats.
   */
  private _onTranscriptionBotMessage(botPeerId: number, message: Uint8Array): void {
    const session = this._session();
    if (!session || message.length > MAX_CORTEX_FRAME_BYTES) return;
    let value: unknown;
    try {
      value = uint8ArrayToValue(message);
    } catch {
      return;
    }
    const frame = parseCortexRoomFrame(value);
    if (frame) this._cortexFrames.next({ roomId: session.roomId, botPeerId, frame });
  }

  private async _onUserDataChanged(payload: UserDataChangedPayload) {
    // Empty data is the plugin's "not decryptable (yet)" after a media key
    // rotation, for a peer still on the old key — not a reset: keep what we
    // know until the peer re-announces under the new key.
    if (payload.peer.data.length === 0) return;
    try {
      const data = uint8ArrayToValue(payload.peer.data);
      this.userService.updateRemoteUserData(payload.peer.userId, data);
    } catch (e) {
      console.error(`Could not decode the UserData from Id: ${payload.peer.userId}`);
    }
  }

  async sendMessage(message: MessageTransfer, target?: User[]) {
    const user = this.userService.ownUser();
    const room = this._odinRoom.getValue();
    if (!user || !room) return;
    const data = valueToUint8Array(message);
    try {
      // ODIN's sendMessage targets are numeric peerIds. Skip recipients
      // who are not currently connected (peerId === null).
      const targetPeerIds = target
        ?.map((u) => u.peerId)
        .filter((p): p is number => typeof p === 'number');
      // ODIN treats empty/undefined `target_peer_ids` as a broadcast. If the
      // caller explicitly named recipients but every one of them filtered
      // out (e.g. all targets have already left and are kept around only for
      // chat history), that's a unicast request with no live receiver —
      // drop it rather than fan it out to the whole room.
      if (target && target.length > 0 && targetPeerIds!.length === 0) {
        return;
      }
      await room.sendMessage(data, targetPeerIds);
      // Only treat a single-recipient send as having a "receiver" (private messages,
      // pokes, mutes). Multi-recipient lists (e.g. a breakout-scoped broadcast) are
      // semantically a broadcast with no single receiver — setting target[0] there
      // would wrongly display and break bundling.
      this.chatService.buildMessage(
        user,
        message,
        target && target.length === 1 ? target[0] : undefined
      );
    } catch (e) {
      console.warn('Could not send the Message');
    }
  }

  updateUserData(userData: UserData) {
    const room = this._odinRoom.getValue();
    if (room?.status.status !== 'joined') return;
    try {
      room.userData = valueToUint8Array(userData);
      room.flushUserData();
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * If successfully, returns the MediaStream. If not, returns null.
   */
  async startCam(): Promise<MediaStream | null> {
    const ms = await this._localStream.startCamStream();
    // A native camera stream is a placeholder without tracks.
    if (!ms || (!isTauri && !ms.getTracks()[0])) return null;
    const currentRoom = this._odinRoom.getValue();
    if (!currentRoom || !this._plugin) return null;
    try {
      const camSettings =
        this._mediaConfigStore.value.cameraSettingsOverride ?? DEFAULT_CAMERA_SETTINGS;
      const capture = await this._plugin.createVideoCapture(ms, {
        codec: camSettings.codec,
        degradationPreference: camSettings.degradationPreference,
        maxBitrate: camSettings.maxBitrate * 1000,
        maxFramerate: camSettings.maxFramerate,
        customType: 'cam',
      });
      const videoInput = new VideoInput(capture);
      await currentRoom.addVideoInput(videoInput);
      this.userService.addCam(videoInput);
      return ms;
    } catch (e) {
      console.error(e);
      this._localStream.stopCamStream('startCam error');
      return null;
    }
  }

  stopCam() {
    const room = this._odinRoom.getValue();
    const user = this.userService.ownUser();
    if (!room || !user || !user.camStream || user.camStream.kind !== 'video-input') {
      return;
    }
    room.removeVideoInput(user.camStream);
    const updatedUser: User = {
      ...user,
      camStream: undefined,
    };
    this.usersStore.update(updatedUser);
  }

  async startScreenCast(ms: MediaStream): Promise<MediaStream | null> {
    const currentRoom = this._odinRoom.getValue();
    if (!currentRoom || !this._plugin || !this.userService.ownUser()) return null;
    try {
      const screenSettings =
        this._mediaConfigStore.value.screenSettingsOverride ?? DEFAULT_SCREEN_SETTINGS;
      const capture = await this._plugin.createVideoCapture(ms, {
        codec: screenSettings.codec,
        degradationPreference: screenSettings.degradationPreference,
        maxBitrate: screenSettings.maxBitrate * 1000,
        maxFramerate: screenSettings.maxFramerate,
        customType: 'screen-cast',
      });
      const videoInput = new VideoInput(capture);
      await currentRoom.addVideoInput(videoInput);
      // Stopped while publishing: stopScreenCast found nothing to remove
      // then, so withdraw the input here instead of leaving it orphaned.
      if (this._localStream.screenCastStream !== ms) {
        currentRoom.removeVideoInput(videoInput);
        return null;
      }
      this.userService.setOwnScreenCast(videoInput);
      return ms;
    } catch (e) {
      console.error(e);
      this._localStream.stopScreenCastStream();
      return null;
    }
  }

  stopScreenCast() {
    // A presentation is a screen share; every way the share ends (dock, the
    // browser's stop bar, a revoked privilege, a failed publish, a reset)
    // lands here, so the stored share and its presentation stamp are always
    // cleared, even when there is no input left to remove.
    const room = this._odinRoom.getValue();
    const screenCast = this.userService.ownUser()?.screenCastStream;
    if (room && screenCast?.kind === 'video-input') {
      room.removeVideoInput(screenCast);
    }
    this.userService.setOwnScreenCast(undefined);
  }

  async playSound(sound: HTMLAudioElement, force?: boolean) {
    if (!this._userActivation.activated()) return;
    if (
      !force &&
      (!this._userSettingsStore.value.playJoinedLeftSounds ||
        !this._userSettingsStore.value.playAppSounds)
    ) {
      return;
    }
    if (sound.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
      const loaded = new Promise<void>((resolve) => {
        sound.addEventListener('loadeddata', () => resolve(), { once: true });
      });
      await Promise.race([loaded, sleep(100)]);
    }
    sound.play().catch((e) => console.error(e));
  }
}
